<template>
  <div>
    <h1 class="title">
      Unlock your ideal prospects <span>with Prospects AI</span>
    </h1>
    <p class="sub-title">
      Share with AI your prospects preferences and get instant, tailored results that will <span>supercharge your sales game!</span>
    </p>
    <tailored-leads-form
      :is-form-loading="isFormLoading"
      @form-submit="handleFormSubmit"
      @duplicate-search="scrollToResults"
    />
    <div
      v-if="totalResults !== null"
      class="results-section"
    >
      <p
        v-if="totalResults > 0"
        class="results-title"
      >
        Prospects AI found <span class="purple-text">{{ totalResults > 100 ? '100+' : totalResults }} tailored prospect{{ totalResults > 1 ? 's' : '' }}</span> for you!
      </p>
      <leads-ai-horizontal-card
        v-for="lead in results"
        :key="lead.id"
        :lead="lead"
      />
    </div>
    <div
      v-if="results.length !== 0 && totalResults > results.length"
      class="more-leads-container"
    >
      <b-button
        :class="{'more-leads-btn': true, 'reduce-padding': !isFetchingNextResults}"
        variant="light"
        :disabled="isFetchingNextResults"
        @click="handleFetchMoreLeads"
      >
        <span v-if="isFetchingNextResults">
          <b-spinner
            class="mr-1"
            small
            label="Loading..."
          /> Loading...
        </span>
        <span v-else>Get {{ remainingLeads }} more tailored prospects
          <span class="credit-block">{{ getLeadsAmount }} <coin-icon /></span>
        </span>
      </b-button>
    </div>
  </div>
</template>
<script>
import store from '@/store'
import { BButton, BSpinner } from 'bootstrap-vue'
import {
 FETCH_AVAILABLE_CREDITS, FETCH_TAILORED_LEADS, GET_ONE_LEAD, SET_AVAILABLE_CREDITS,
 SET_REQUEST_SENT_FLAG,
} from '@/store/modules/leadai.module'
import { apiToastWarning } from '@/@core/utils/toast'
import { onBeforeUnmount } from '@vue/composition-api'
import analytics from '@/@core/utils/analytics'
import constants from '@/constants'
import CoinIcon from '@/@core/assets/svg-components/CoinIcon.vue'
import TailoredLeadsForm from './TailoredLeadsForm.vue'
import LeadsAiHorizontalCard from '../../LeadsAiHorizontalCard/LeadsAiHorizontalCard.vue'
import { desiredKeywordsMatchRuleMap, ERROR_MESSAGES, TAILORED_LEADS_FORM_STORAGE_KEY } from '../../leadsAiConstants'
import { handleErrorToast } from '../../utils/handleErrorToast'

export default {
  name: 'LeadsAiTab',
  components: {
    TailoredLeadsForm,
    BButton,
    LeadsAiHorizontalCard,
    BSpinner,
    CoinIcon,
  },
  data() {
    return {
      isFormLoading: false,
      results: [],
      totalResults: null,
      formData: null,
      page: 1,
      limit: 5,
      searchAfter: '',
      isFetchingNextResults: false,
    }
  },
  computed: {
    creditRates() {
      return store.state.leadai.currentCreditRates
    },
    remainingLeads() {
      return this.isLastPage ? this.lastPageCount : this.limit
    },
    getLeadsAmount() {
      return (this.isLastPage ? this.lastPageCount : this.limit) * 2
    },
    isLastPage() {
      const currentPage = this.page - 1
      const totalAvailablePages = Math.ceil(this.totalResults / this.limit)
      return currentPage === totalAvailablePages - 1
    },
    lastPageCount() {
      return this.totalResults - (this.page - 1) * this.limit
    },
  },
  created() {
    const existingData = sessionStorage.getItem(TAILORED_LEADS_FORM_STORAGE_KEY)
    if (existingData) {
      const persistedResults = JSON.parse(existingData)
      this.results = persistedResults?.results
      this.page = persistedResults?.page
      this.totalResults = persistedResults?.totalResults
      this.searchAfter = persistedResults?.searchAfter
    }

    this.setFormDataFromStorage()
  },
  methods: {
    async fetchLeadDetails(lead) {
      try {
        const res = await store.dispatch(GET_ONE_LEAD, {
          domain: lead?.domain,
          storeName: lead?.storeName,
        })
        if (res?.data) {
          this.$router.push({ name: 'prospects-gen-ai-detail', params: { prospectId: res?.data?.id } })
        }
      } catch (err) {
        this.handleFetchLeadDetailError(err)
      }
    },
    setFormDataFromStorage() {
      const existingData = localStorage.getItem(TAILORED_LEADS_FORM_STORAGE_KEY)
      if (existingData) {
        const persistedInputs = JSON.parse(existingData) ?? {}
        this.formData = persistedInputs?.inputs
      }
    },
    handleFetchLeadDetailError(err) {
      if (err.response?.status === 410) {
        this.$bvModal.show('modal-lead-details')
        this.error = err.response.data?.details?.leadsAiType || 'Unknown error'
      } else {
        handleErrorToast(err, err.response?.message || ERROR_MESSAGES.FETCH_LEAD_DETAIL_FAILED)
      }
    },
    handleFetchMoreLeads() {
      analytics.track(constants.TRACKS.ACTIONS.LEAD_AI.BRAND_CLICKS_GET_MORE_LEADS_BUTTON, {
        formData: this.formData,
        currentLeadsCount: this.results?.length,
      })
      this.fetchCompaniesResponse()
    },
    async fetchCompaniesResponse() {
      if (this.formData) {
        try {
          this.isFetchingNextResults = true
          const payload = {
            ...this.formData,
            matchRule: desiredKeywordsMatchRuleMap[this.formData.matchRule[0]],
            relevancyScore: Array.isArray(this.formData.relevancyScore) ? Number(this.formData.relevancyScore[0].replace('%', '')) / 100 : this.formData.relevancyScore,
            page: this.page,
            limit: this.limit,
            previousLeads: [...this.results.map(lead => lead.domain)],
          }
          if (this.searchAfter) {
            payload.searchAfter = this.searchAfter
          }
          const apiResponse = await store.dispatch(FETCH_TAILORED_LEADS, payload)
          this.handleApiResponse(apiResponse)
        } catch (err) {
          handleErrorToast(err, ERROR_MESSAGES.FETCH_TAILORED_LEADS_FAILED)
          analytics.track(constants.TRACKS.ACTIONS.LEAD_AI.BRAND_GET_TAILORED_LEADS_FAILED, {
            formData: this.formData,
            error: err ? err?.message ?? 'Something went wrong' : undefined,
          })
        } finally {
          this.isFormLoading = false
          this.isFetchingNextResults = false
        }
      }
    },
    async fetchAvailableCredits() {
      const availableCreditsResponse = await store.dispatch(FETCH_AVAILABLE_CREDITS, {
        brandId: store.getters.currentEntityId,
      })

      if (availableCreditsResponse?.data) {
        store.commit(SET_AVAILABLE_CREDITS, availableCreditsResponse.data?.availableCredits)
      }
    },
    handleApiResponse(apiResponse) {
      analytics.track(constants.TRACKS.ACTIONS.LEAD_AI.BRAND_GET_TAILORED_LEADS_SUCCESS, {
        formData: this.formData,
        response: apiResponse,
      })
      this.checkForViewedLeads(apiResponse.data.companies)
      this.fetchAvailableCredits()
      if (apiResponse.data?.totalCount === 0) {
        this.isFormLoading = false

        apiToastWarning('No prospects found. Please try with different inputs.')
        return
      }
      const updatedCompaniesData = apiResponse.data.companies
      this.results.push(...updatedCompaniesData)
      this.totalResults = apiResponse.data?.totalCount ?? this.totalResults
      this.isFormLoading = false
      if (this.page === 1) {
        if (apiResponse.data?.searchAfter) {
          this.searchAfter = apiResponse.data?.searchAfter
        }
        setTimeout(() => this.scrollToResults(), 100)
      }
      this.page++
      this.isFetchingNextResults = false
      this.handleDataPersistence()
    },
    handleDataPersistence() {
      const existingData = sessionStorage.getItem(TAILORED_LEADS_FORM_STORAGE_KEY)

      const persistedData = existingData ? JSON.parse(existingData) : {}

      sessionStorage.setItem(TAILORED_LEADS_FORM_STORAGE_KEY, JSON.stringify({
        ...persistedData,
        results: this.results,
        totalResults: this.totalResults,
        page: this.page,
        searchAfter: this.searchAfter,
      }))
      store.commit(SET_REQUEST_SENT_FLAG, true)
    },
    checkForViewedLeads(leads) {
      if (leads.some(lead => lead.viewed)) {
        apiToastWarning('You were only charged for new prospects.')
      }
    },
    async handleFormSubmit(data) {
      this.isFormLoading = true
      this.formData = data
      this.page = 1
      this.results = []
      this.totalResults = null

      await this.fetchCompaniesResponse()
    },
    scrollToResults() {
      const resultSection = document.getElementsByClassName('results-section')

      if (resultSection?.length) {
        window.scrollBy({ top: 560, behavior: 'smooth' })
      }
    },
  },
  setup() {
    const startTime = Date.now()

    onBeforeUnmount(() => {
      analytics.track(constants.TRACKS.PAGES.BRAND_VIEWS_TAB_DATA.replace('<%tabName%>', 'Leads AI tab'), {
        tabName: 'Leads AI tab',
        timeSpent: `${(Date.now() - startTime) / 1000 / 60} minutes`,
      })
    })
  },
}
</script>
<style scoped lang="scss">
@import '@/@core/scss/base/bootstrap-extended.scss';

.title {
  font-size: 32.471px;
  font-weight: normal;
  color: $body-color;
  margin-top: 23px;
  line-height: 42px;
  margin-bottom: 9px;
  position: relative;
  left: -2px;
  span {
    color: #8d82fd;
    font-weight: initial;
  }
}

.sub-title {
  font-size: 18px;
  max-width: 575px;
  margin-bottom: 22px;

  span {
    color: #8d82fd;
    font-weight: bold;
    line-height: 23px;
  }
}

.results-section {
  .results-title {
    font-size: 18px;
    line-height: 23.5px;
    margin-top: 29px;
    margin-bottom: 23px;

    span.purple-text {
      color: #8d82fd;
      font-weight: 700;
    }
  }
}

.more-leads-container {
  margin-top: 40px;
  margin-bottom: 50px;
  display: flex;
  justify-content: center;

  button.more-leads-btn:disabled {
    cursor: not-allowed;
  }
  .more-leads-btn {
    background-color: $custom-secondary !important;
    color: $text-color-v2 !important;
    border: none;
    font-weight: 400;

    .credit-block {
      display: inline-flex;
      color: #686767;
      font-size: 12px;
      line-height: 17px;
      border-radius: 5px;
      border: 1px solid #7163FF;
      padding: 3px 6px;
      align-items: center;
      gap: 4px;
      margin-left: 8px;
    }
  }

  .reduce-padding {
    padding-top: 5px;
    padding-bottom: 5px;
  }
}
</style>
